import { TFunction } from 'i18next';

/**
 * Extrae parámetros numéricos del mensaje de error original
 * Ejemplo: "Quantity done (10) plus quantity waste (10) must equal expected quantity (100)"
 * Retorna: { qty_done: 10, qty_waste: 10, qty_expected: 100 }
 */
const extractParamsFromMessage = (message: string): Record<string, string | number> => {
  const params: Record<string, string | number> = {};

  // PRIMERO: Buscar patrones específicos con "ID" seguido de número
  // Product with ID 1427 does not belong to team ID 63.
  if (message.includes('Product') && message.includes('does not belong to team')) {
    const productMatch = message.match(/Product\s+with\s+ID\s+(\d+)/i);
    const teamMatch = message.match(/team\s+ID\s+(\d+)/i);
    if (productMatch) {
      params.product_id = parseInt(productMatch[1], 10);
    }
    if (teamMatch) {
      params.team_id = parseInt(teamMatch[1], 10);
    }
    return params; // Retornar temprano si encontramos este patrón
  }

  // Location with ID {location_id} does not belong to team ID {team_id}.
  if (message.includes('Location') && message.includes('does not belong to team')) {
    const locationMatch = message.match(/Location\s+with\s+ID\s+(\d+)/i);
    const teamMatch = message.match(/team\s+ID\s+(\d+)/i);
    if (locationMatch) {
      params.location_id = parseInt(locationMatch[1], 10);
    }
    if (teamMatch) {
      params.team_id = parseInt(teamMatch[1], 10);
    }
    return params; // Retornar temprano si encontramos este patrón
  }

  // SEGUNDO: Buscar patrones con valores entre paréntesis (10), (100), etc.
  const matches = message.match(/\((\d+)\)/g);
  if (matches) {
    const values = matches.map((m) => parseInt(m.replace(/[()]/g, ''), 10));

    // Intentar identificar qué valor corresponde a qué parámetro basándose en el contexto del mensaje
    if (message.includes('done') && message.includes('waste') && message.includes('expected')) {
      // Formato: "Quantity done (X) plus quantity waste (Y) must equal expected quantity (Z)"
      if (values.length >= 3) {
        params.qty_done = values[0];
        params.qty_waste = values[1];
        params.qty_expected = values[2];
      }
    } else if (message.includes('location_id') || message.includes('cart')) {
      // Buscar location_id en el mensaje
      const locationMatch = message.match(/(?:ID|id|location_id)[\s:]+(\d+)|location_id[:\s]+(\d+)/i);
      if (locationMatch) {
        params.location_id = parseInt(locationMatch[1] || locationMatch[2], 10);
      } else if (values.length > 0) {
        params.location_id = values[0];
      }
    } else if (message.includes('team')) {
      // Buscar team_id
      const teamMatch = message.match(/team\s+ID\s+(\d+)|team_id[:\s]+(\d+)/i);
      if (teamMatch) {
        params.team_id = parseInt(teamMatch[1] || teamMatch[2], 10);
      }
    }

    // Buscar ID genérico si no se encontró nada específico
    if (Object.keys(params).length === 0 && values.length > 0) {
      const idMatch = message.match(/(?:order|line|work order)\s+(\d+)|ID\s+(\d+)|id\s+(\d+)/i);
      if (idMatch) {
        params.id = parseInt(idMatch[1] || idMatch[2] || idMatch[3], 10);
      } else {
        params.id = values[0];
      }
    }
  }

  // TERCERO: Buscar IDs directamente en el texto sin paréntesis
  // Buscar "ID {número}" o "id {número}" en cualquier parte del mensaje
  const idPatterns = [
    /Product\s+with\s+ID\s+(\d+)/i,
    /Location\s+with\s+ID\s+(\d+)/i,
    /team\s+ID\s+(\d+)/i,
    /location\s+ID\s+(\d+)/i,
    /cart\s+ID[:\s]+(\d+)/i,
    /ID\s+(\d+)/i,
  ];

  for (const pattern of idPatterns) {
    const match = message.match(pattern);
    if (match) {
      const value = parseInt(match[1], 10);
      if (pattern.source.includes('Product')) {
        params.product_id = value;
      } else if (pattern.source.includes('Location') || pattern.source.includes('location')) {
        params.location_id = value;
      } else if (pattern.source.includes('team')) {
        params.team_id = value;
      } else if (pattern.source.includes('cart')) {
        params.location_id = value; // cart_id se mapea a location_id
      } else {
        // ID genérico
        if (!params.id) {
          params.id = value;
        }
      }
    }
  }

  return params;
};

/**
 * Traduce un error de la API a un mensaje internacionalizado
 *
 * @param error - Error de la API con formato { error: { error_code, message, field, type } }
 * @param t - Función de traducción de i18next
 * @returns Mensaje traducido
 */
export const translateApiError = (error: any, t: TFunction): string => {
  console.log('🔍 translateApiError - Error recibido:', error);
  console.log('🔍 translateApiError - error.response?.data:', error?.response?.data);

  // Obtener el objeto error de la respuesta
  // La API devuelve: { error: { error_code, message, field, type } }
  const errorObj = error?.response?.data?.error;

  console.log('🔍 translateApiError - errorObj:', errorObj);

  // Si no hay objeto error, intentar obtener el mensaje directamente
  if (!errorObj) {
    const fallbackMessage =
      error?.response?.data?.message || error?.message || t('toasts.error.unexpected', 'Error inesperado');
    console.log('🔍 translateApiError - No hay errorObj, usando mensaje fallback:', fallbackMessage);
    return fallbackMessage;
  }

  const errorCode = errorObj.error_code;
  const originalMessage = errorObj.message || '';
  const field = errorObj.field || '';
  const type = errorObj.type || '';

  console.log(
    '🔍 translateApiError - errorCode:',
    errorCode,
    'originalMessage:',
    originalMessage,
    'field:',
    field,
    'type:',
    type,
  );

  // PRIMERO: Intentar traducir usando el error_code (tiene prioridad)
  if (errorCode) {
    const translationKey = `toasts.error.codes.${errorCode}`;
    console.log('🔍 translateApiError - Buscando traducción con key:', translationKey);

    // Intentar obtener la traducción sin fallback para detectar si existe
    // Si no existe, i18next devuelve la clave misma o null
    const translated = t(translationKey);

    console.log('🔍 translateApiError - Traducción devuelta por t():', translated);
    console.log('🔍 translateApiError - Clave buscada:', translationKey);
    console.log('🔍 translateApiError - ¿Son iguales?', translated === translationKey);

    // Si la traducción es diferente a la clave y no está vacía, significa que se encontró
    const isTranslated = translated && translated !== translationKey && translated !== '';

    if (isTranslated) {
      console.log('✅ translateApiError - Traducción encontrada!');

      // Extraer parámetros del mensaje original
      const params = extractParamsFromMessage(originalMessage);
      console.log('🔍 translateApiError - Parámetros extraídos:', params);

      // Reemplazar placeholders en la traducción
      let finalMessage = translated;
      if (Object.keys(params).length > 0) {
        Object.keys(params).forEach((key) => {
          const regex = new RegExp(`\\{${key}\\}`, 'g');
          const value = String(params[key]);
          const beforeReplace = finalMessage;
          finalMessage = finalMessage.replace(regex, value);
          if (beforeReplace !== finalMessage) {
            console.log(`✅ translateApiError - Reemplazado {${key}} con ${value}`);
          }
        });
      }

      console.log('✅ translateApiError - Mensaje final traducido:', finalMessage);
      return finalMessage;
    } else {
      console.warn('⚠️ translateApiError - No se encontró traducción para error_code:', translationKey);
      console.warn('⚠️ translateApiError - Valor devuelto:', translated);
    }
  }

  // SEGUNDO: Si no hay error_code o no se encontró traducción, intentar con validación de Laravel
  // SOLO si NO hay error_code (los error_code tienen prioridad)
  if (!errorCode && type === 'validation' && field) {
    const validationKey = `${field}_${originalMessage
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '_')
      .replace(/^the_|_field|_is_|_must_|_be_|_an_|_at_|_least_|_selected_/g, '')}`;
    const translated = t(`toasts.error.codes.validation.${validationKey}`, null);

    if (translated && translated !== validationKey) {
      return translated;
    }

    // Fallback: intentar con claves más específicas
    if (field === 'id' && originalMessage.includes('invalid')) {
      return t('toasts.error.codes.validation.id_invalid', 'El ID seleccionado no es válido.');
    }
    if (field === 'id' && originalMessage.includes('required')) {
      return t('toasts.error.codes.validation.id_required', 'El campo ID es requerido.');
    }
    if (field === 'id' && originalMessage.includes('integer')) {
      return t('toasts.error.codes.validation.id_must_be_integer', 'El ID debe ser un número entero.');
    }
    if (field === 'line_id' && originalMessage.includes('invalid')) {
      return t('toasts.error.codes.validation.line_id_invalid', 'El ID de línea seleccionado no es válido.');
    }
    if (field === 'line_id' && originalMessage.includes('required')) {
      return t('toasts.error.codes.validation.line_id_required', 'El campo ID de línea es requerido.');
    }
    if (field === 'line_id' && originalMessage.includes('integer')) {
      return t('toasts.error.codes.validation.line_id_must_be_integer', 'El ID de línea debe ser un número entero.');
    }
    if (field === 'quantity_completed' && originalMessage.includes('required')) {
      return t(
        'toasts.error.codes.validation.quantity_completed_required',
        'El campo cantidad completada es requerido.',
      );
    }
    if (field === 'quantity_completed' && originalMessage.includes('at least 1')) {
      return t('toasts.error.codes.validation.quantity_completed_min', 'La cantidad completada debe ser al menos 1.');
    }
    if (field === 'quantity_completed' && originalMessage.includes('integer')) {
      return t(
        'toasts.error.codes.validation.quantity_completed_must_be_integer',
        'La cantidad completada debe ser un número entero.',
      );
    }
    if (field === 'location_id' && originalMessage.includes('required')) {
      return t('toasts.error.codes.validation.location_id_required', 'El campo ID de ubicación es requerido.');
    }
    if (field === 'location_id' && originalMessage.includes('invalid')) {
      return t('toasts.error.codes.validation.location_id_invalid', 'El ID de ubicación seleccionado no es válido.');
    }
    if (field === 'location_id' && originalMessage.includes('integer')) {
      return t(
        'toasts.error.codes.validation.location_id_must_be_integer',
        'El ID de ubicación debe ser un número entero.',
      );
    }
    if (field === 'location_code' && originalMessage.includes('required')) {
      return t('toasts.error.codes.validation.location_code_required', 'El campo código de ubicación es requerido.');
    }
    if (field === 'location_code' && originalMessage.includes('invalid')) {
      return t(
        'toasts.error.codes.validation.location_code_invalid',
        'El código de ubicación seleccionado no es válido.',
      );
    }
  }

  // Fallback: usar el mensaje original si no hay traducción
  console.log('🔍 translateApiError - Fallback: usando mensaje original en inglés');
  return originalMessage || t('toasts.error.unexpected', 'Error inesperado');
};
